import { derived, get, type Readable, type Updater, type Writable } from "svelte/store";
import { ExternalDestinationSchema, LandingPageSchema, SuiteSchema, UtmParametersSchema, type ExternalDestination, type LandingPage, type Suite } from "#gen/bundles/v1/bundle_pb.js";
import { Mode } from "#gen/bundles/v1/service_pb.js";
import { PageSchema, type Page } from "#gen/pages/v2/page_pb.js";
import type { EntryAsset } from "#lib/assetManager.js";
import { deepClone } from "#lib/deepClone.js";
import { clone, fromJson, toJson, toJsonString, type JsonObject } from "#lib/protobuf-compat.js";
import { Provider } from "#lib/provider.js";
import { replaceAfterFirstMatch, snakeCase } from "#lib/string.js";
import type { TokenProvider } from "#lib/tokenProvider.js";
import { smsProtocol, telProtocol } from "#lib/url.js";
import type { FcGeneratorOptions } from "@dtx-company/flowcode-generator-v2";

export function interpolate(suite: Suite, entryPoint: Writable<EntryAsset | undefined>, str: string, contactProperties?: JsonObject): string {
	const metadata = get(entryPoint)?.metadata ?? ({} as JsonObject);
	const rendered = JSON.parse(contactProperties ? interpolateSuiteData(JSON.stringify({ str }), toJsonString(SuiteSchema, suite), JSON.stringify(metadata), "{}", JSON.stringify(contactProperties)) : interpolateSuiteData(JSON.stringify({ str }), toJsonString(SuiteSchema, suite), JSON.stringify(metadata), "{}"));
	return rendered.str;
}

export function createInterpolatedPageStore(tokenProvider: Readable<TokenProvider>, suite: Readable<Suite>, entryPoint: Writable<EntryAsset | undefined>, store: Readable<Page>, mode: Mode): Readable<Page> {
	if (!store || mode !== Mode.EDITING) {
		return store;
	}
	function render(provider: TokenProvider, value: Page | undefined, metadata: JsonObject, suite: Suite) {
		if (!value?.config) {
			return undefined;
		}
		return doRender(provider, JSON.stringify(value.config), metadata, suite);
	}
	function interpolate(value: [TokenProvider, EntryAsset | undefined, Page, Suite], set: ($value: Page) => void, _update: (fn: Updater<Page>) => void) {
		const metadata = value[1]?.metadata ?? ({} as JsonObject);
		const result = render(value[0], value[2], metadata, value[3]);
		const page = clone(PageSchema, value[2]);
		if (result) {
			page.config = result;
		}
		set(page);
	}
	const initialValue = clone(PageSchema, get(store));
	const provider = get(tokenProvider);
	const metadata = get(entryPoint)?.metadata ?? ({} as JsonObject);
	const rendered = render(provider, initialValue, metadata, get(suite));
	if (rendered) {
		initialValue.config = rendered;
	}
	return derived([tokenProvider, entryPoint, store, suite], interpolate, initialValue);
}

export function createInterpolatedDataStore(tokenProvider: Readable<TokenProvider>, suite: Readable<Suite>, entryPoint: Writable<EntryAsset | undefined>, store: Readable<JsonObject | undefined>, mode: Mode): Readable<JsonObject | undefined> {
	if (!store || mode !== Mode.EDITING) {
		return store;
	}
	function render(provider: TokenProvider, value: JsonObject | undefined, metadata: JsonObject, suite: Suite) {
		if (!value) {
			return undefined;
		}
		return doRender(provider, JSON.stringify(value), metadata, suite);
	}
	function interpolate(value: [TokenProvider, EntryAsset | undefined, JsonObject | undefined, Suite], set: ($value: JsonObject | undefined) => void) {
		const metadata = value[1]?.metadata ?? ({} as JsonObject);
		const result = render(value[0], value[2], metadata, value[3]);
		if (result) {
			set(result);
		}
	}
	const initialValue = deepClone(get(store));
	const provider = get(tokenProvider);
	const metadata = get(entryPoint)?.metadata ?? ({} as JsonObject);
	const rendered = render(provider, initialValue, metadata, get(suite));
	return derived([tokenProvider, entryPoint, store, suite], interpolate, rendered ?? initialValue);
}

export function resolveURL(destination: ExternalDestination): string {
	if (!destination.url) {
		return "";
	}
	try {
		const raw = destination.url;
		const questionMark = raw.indexOf("?");
		const baseURL = questionMark === -1 ? raw : raw.slice(0, questionMark);
		const params = new URLSearchParams(questionMark === -1 ? "" : raw.slice(questionMark + 1));

		const utmParams = (toJson(UtmParametersSchema, destination.utmParameters) as JsonObject) ?? {};
		for (const name of Object.keys(utmParams).toSorted((a, b) => a.localeCompare(b))) {
			params.set(snakeCase(name), utmParams[name] as string);
		}

		const qs = params.toString().replaceAll("+", "%20");
		const resolved = qs ? `${baseURL}?${qs}` : baseURL;

		if (raw.startsWith(smsProtocol) || raw.startsWith(telProtocol)) {
			// There is a valid + in the URI containing phone numbers that should not be encoded
			return replaceAfterFirstMatch(resolved, "+", "%20");
		}
		return resolved;
	} catch {
		return destination.url;
	}
}

export function createInterpolatedExternalDestinationStore(tokenProvider: Readable<TokenProvider>, suite: Readable<Suite>, entryPoint: Writable<EntryAsset | undefined>, store: Readable<ExternalDestination>, mode: Mode): Readable<ExternalDestination> {
	if (!store || mode !== Mode.EDITING) {
		return store;
	}
	function render(provider: TokenProvider, value: ExternalDestination | undefined, metadata: JsonObject, suite: Suite) {
		if (!value) {
			return undefined;
		}
		const rendered = doRender(provider, JSON.stringify(value), metadata, suite);
		return fromJson(ExternalDestinationSchema, rendered, { ignoreUnknownFields: true });
	}
	function interpolate(value: [TokenProvider, EntryAsset | undefined, ExternalDestination, Suite], set: ($value: ExternalDestination) => void) {
		const metadata = value[1]?.metadata ?? ({} as JsonObject);
		const result = render(value[0], value[2], metadata, value[3]);
		if (result) {
			result.resolvedUrl = resolveURL(result);
			set(result);
		}
	}
	const initialValue = clone(ExternalDestinationSchema, get(store));
	const provider = get(tokenProvider);
	const metadata = get(entryPoint)?.metadata ?? ({} as JsonObject);
	const rendered = render(provider, initialValue, metadata, get(suite));
	return derived([tokenProvider, entryPoint, store, suite], interpolate, rendered ?? initialValue);
}

export function createInterpolatedLandingPageStore(tokenProvider: Readable<TokenProvider>, suite: Readable<Suite>, entryPoint: Writable<EntryAsset | undefined>, store: Readable<LandingPage>, mode: Mode): Readable<LandingPage> {
	if (!store || mode !== Mode.EDITING) {
		return store;
	}
	function render(provider: TokenProvider, value: LandingPage | undefined, metadata: JsonObject, suite: Suite) {
		if (!value) {
			return undefined;
		}
		const obj = toJson(LandingPageSchema, value) as JsonObject;
		const imagesKey = obj.imagesKey as string | undefined;
		if (imagesKey) {
			if (imagesKey.startsWith("{{") && imagesKey.endsWith("}}")) {
				obj.images = imagesKey;
			}
			delete obj.imagesKey;
		}
		const rendered = doRender(provider, JSON.stringify(obj), metadata, suite);
		if (imagesKey) {
			rendered.imagesKey = imagesKey;
		}
		return fromJson(LandingPageSchema, rendered, { ignoreUnknownFields: true });
	}
	function interpolate(value: [TokenProvider, EntryAsset | undefined, LandingPage, Suite], set: ($value: LandingPage) => void) {
		const metadata = value[1]?.metadata ?? ({} as JsonObject);
		const result = render(value[0], value[2], metadata, value[3]);
		if (result) {
			set(result);
		}
	}
	const initialValue = clone(LandingPageSchema, get(store));
	const provider = get(tokenProvider);
	const metadata = get(entryPoint)?.metadata ?? ({} as JsonObject);
	const rendered = render(provider, initialValue, metadata, get(suite));
	return derived([tokenProvider, entryPoint, store, suite], interpolate, rendered ?? initialValue);
}

export function createInterpolatedConfigStore(tokenProvider: Readable<TokenProvider>, suite: Readable<Suite>, entryPoint: Writable<EntryAsset | undefined>, store: Readable<FcGeneratorOptions>, mode: Mode): Readable<FcGeneratorOptions> {
	if (!store || mode !== Mode.EDITING) {
		return store;
	}
	function render(provider: TokenProvider, value: FcGeneratorOptions | undefined, metadata: JsonObject, suite: Suite) {
		if (!value) {
			return undefined;
		}
		return doRender(provider, JSON.stringify(value), metadata, suite);
	}
	function interpolate(value: [TokenProvider, EntryAsset | undefined, FcGeneratorOptions, Suite], set: ($value: FcGeneratorOptions) => void) {
		const metadata = value[1]?.metadata ?? ({} as JsonObject);
		const result = render(value[0], value[2], metadata, value[3]);
		if (result) {
			set(result);
		}
	}
	const initialValue = deepClone(get(store));
	const provider = get(tokenProvider);
	const metadata = get(entryPoint)?.metadata ?? ({} as JsonObject);
	const rendered = render(provider, initialValue, metadata, get(suite));
	return derived([tokenProvider, entryPoint, store, suite], interpolate, rendered ?? initialValue);
}

export function createInterpolatedSuiteStore(tokenProvider: Readable<TokenProvider>, suite: Readable<Suite>, entryPoint: Writable<EntryAsset | undefined>, mode: Mode): Readable<Suite> {
	if (mode !== Mode.EDITING) {
		return suite;
	}
	function render(provider: TokenProvider, metadata: JsonObject, suite: Suite) {
		return doRender(provider, JSON.stringify(toJson(SuiteSchema, suite)), metadata, suite);
	}
	function interpolate(value: [TokenProvider, EntryAsset | undefined, Suite], set: ($value: Suite) => void, _update: (fn: Updater<Suite>) => void) {
		const metadata = value[1]?.metadata ?? ({} as JsonObject);
		const rendered = render(value[0], metadata, value[2]);
		set(fromJson(SuiteSchema, rendered));
	}
	const initialValue = clone(SuiteSchema, get(suite));
	const metadata = get(entryPoint)?.metadata ?? ({} as JsonObject);
	const rendered = render(get(tokenProvider), metadata, initialValue);
	return derived([tokenProvider, entryPoint, suite], interpolate, fromJson(SuiteSchema, rendered));
}

function doRender(provider: TokenProvider, value: string, metadata: JsonObject, suite: Suite) {
	if (provider.hasSubstitutionToken(value)) {
		const request = provider.generateSampleValues([Provider.FORM])["requestData"] ?? {};
		const response = provider.generateSampleValues([Provider.LOOKUP, Provider.AGENT])["responseData"] ?? {};
		const additional = provider.generateSampleValues([Provider.CONTACT_PROPERTY, Provider.EVENT]) ?? {};
		return JSON.parse(interpolateSuiteData(value, toJsonString(SuiteSchema, suite), JSON.stringify(metadata), JSON.stringify(request), JSON.stringify(response), JSON.stringify(additional)));
	}
	return JSON.parse(value);
}
